import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import SignaturePad from "../components/SignaturePad";
import AuthImg from "../components/pilotage/AuthImg";
import { api } from "../lib/api";
import { Loader2, ChevronRight, ChevronLeft, Camera, Fuel, Gauge, AlertTriangle, CheckCircle2, X, Printer, BatteryCharging, ShieldCheck, Wrench, ArrowUpRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const FUEL_LEVELS = ["1/4", "1/2", "3/4", "Plein"];
const STEPS = ["Kilométrage", "Photos", "Dommages", "Signature"];

export default function Inspection() {
  const { bookingId, type } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [inspection, setInspection] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/inspections/${bookingId}/${type}`)
      .then(({ data }) => { setInspection(data.inspection); setRole(data.role); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [bookingId, type]);

  if (loading) return <Shell><div className="py-32 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div></Shell>;
  if (!inspection) return (
    <Shell>
      <div className="py-20 px-5 text-center max-w-md mx-auto" data-testid="inspection-not-found">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
          <ShieldCheck className="h-7 w-7 text-slate-400" />
        </div>
        <h2 className="font-heading text-lg font-semibold text-slate-900 mb-1">État des lieux introuvable</h2>
        <p className="text-sm text-slate-500 mb-6">
          Cet état des lieux n'existe pas encore ou le lien est invalide. Il peut être créé depuis la fiche de la réservation.
        </p>
        <button
          onClick={() => navigate(`/vendor/bookings/${bookingId}`)}
          className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white text-sm font-semibold px-5 py-2.5 hover:bg-slate-800 transition-colors"
          data-testid="inspection-not-found-back-btn"
        >
          Ouvrir la réservation
        </button>
      </div>
    </Shell>
  );

  const isVendor = role === "vendor";
  const alreadySigned = role === "client" ? !!inspection.client_signature : !!inspection.vendor_signature;
  const isLocked = !!inspection.client_signature || !!inspection.vendor_signature;
  const typeLabel = type === "departure" ? "Départ" : "Retour";

  return (
    <Shell>
      <div className="max-w-2xl mx-auto px-5 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="overline">État des lieux · {typeLabel}</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-slate-900 mt-1" data-testid="inspection-title">
            État des lieux {type === "departure" ? "de départ" : "de retour"}
          </h1>
          {inspection.reference && (
            <div className="mt-1 text-xs font-mono text-slate-400" data-testid="inspection-reference">{inspection.reference}</div>
          )}
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-1 mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-violet-600" : "bg-slate-200"}`} />
              <div className={`text-[10px] mt-1 text-center font-medium ${i <= step ? "text-violet-600" : "text-slate-400"}`}>{s}</div>
            </div>
          ))}
        </div>

        {/* Steps */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 min-h-[350px]" data-testid="inspection-step">
          {step === 0 && <StepMileage inspection={inspection} setInspection={setInspection} disabled={isLocked} />}
          {step === 1 && <StepPhotos inspection={inspection} setInspection={setInspection} disabled={isLocked} />}
          {step === 2 && <StepDamages inspection={inspection} setInspection={setInspection} disabled={isLocked} />}
          {step === 3 && <StepSign inspection={inspection} setInspection={setInspection} role={role} alreadySigned={alreadySigned} />}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-30"
            data-testid="step-prev"
          >
            <ChevronLeft className="h-4 w-4" /> Précédent
          </button>
          {step < 3 ? (
            <button
              onClick={async () => {
                if (!isLocked && step === 0) await saveMileage(inspection);
                if (!isLocked && step === 2) await saveDamages(inspection);
                setStep(step + 1);
              }}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition-colors"
              data-testid="step-next"
            >
              Suivant <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={() => navigate(`/contract/${bookingId}`)}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
              data-testid="back-to-contract"
            >
              Retour au contrat
            </button>
          )}
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen bg-slate-50"><PublicNavbar />{children}</div>;
}

// Save helpers
async function saveMileage(insp) {
  try {
    await api.put(`/inspections/${insp.id}`, {
      step: "mileage", mileage: insp.mileage, fuel_level: insp.fuel_level,
      battery_level: insp.battery_level ?? null,
    });
  } catch (err) {
    console.warn("Inspection: saveMileage failed", err);
  }
}
async function saveDamages(insp) {
  try {
    await api.put(`/inspections/${insp.id}`, { step: "damages", zones: insp.zones, general_comment: insp.general_comment });
  } catch (err) {
    console.warn("Inspection: saveDamages failed", err);
  }
}

// ─── Step 1: Mileage & Fuel / Battery ───
function StepMileage({ inspection, setInspection, disabled }) {
  const energy = (inspection.vehicle_energy || "").toLowerCase();
  const isElectric = energy.includes("électrique") || energy.includes("electrique");
  const isHybrid = energy.includes("hybride");
  const showFuel = !isElectric || isHybrid;
  const showBattery = isElectric || isHybrid;
  return (
    <div className="space-y-6" data-testid="step-mileage">
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-slate-900 mb-2">
          <Gauge className="h-4 w-4 text-violet-600" /> Kilométrage
        </label>
        <input
          type="number"
          value={inspection.mileage || ""}
          onChange={(e) => setInspection({ ...inspection, mileage: parseInt(e.target.value) || null })}
          disabled={disabled}
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-lg font-medium focus:ring-2 focus:ring-violet-600 focus:border-violet-600 outline-none"
          placeholder="Ex: 45230"
          data-testid="mileage-input"
        />
      </div>
      {showFuel && (
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-900 mb-3">
            <Fuel className="h-4 w-4 text-violet-600" /> Niveau de carburant
          </label>
          <div className="flex gap-2">
            {FUEL_LEVELS.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => !disabled && setInspection({ ...inspection, fuel_level: l })}
                disabled={disabled}
                className={`flex-1 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                  inspection.fuel_level === l
                    ? "border-violet-600 bg-violet-50 text-violet-700"
                    : "border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
                data-testid={`fuel-${l}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      )}
      {showBattery && (
        <div data-testid="battery-block">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-900 mb-3">
            <BatteryCharging className="h-4 w-4 text-emerald-600" /> Niveau de batterie ({inspection.battery_level ?? "—"} %)
          </label>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={inspection.battery_level ?? 50}
            onChange={(e) => setInspection({ ...inspection, battery_level: parseInt(e.target.value) })}
            disabled={disabled}
            className="w-full accent-emerald-600"
            data-testid="battery-slider"
          />
          <div className="flex justify-between text-[10px] text-slate-400 mt-1">
            <span>0 %</span><span>25 %</span><span>50 %</span><span>75 %</span><span>100 %</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step 2: Photos ───
function StepPhotos({ inspection, setInspection, disabled }) {
  const fileRef = useRef(null);
  const [activeZone, setActiveZone] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeZone) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(`/inspections/${inspection.id}/photo?zone=${encodeURIComponent(activeZone)}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const zones = inspection.zones.map((z) =>
        z.name === activeZone ? { ...z, photos: [...z.photos, data.path] } : z
      );
      setInspection({ ...inspection, zones });
    } catch {
      alert("Erreur lors de l'upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (zoneName, path) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await api.delete(`/inspections/${inspection.id}/photo?zone=${encodeURIComponent(zoneName)}&path=${encodeURIComponent(path)}`);
      const zones = inspection.zones.map((z) =>
        z.name === zoneName ? { ...z, photos: z.photos.filter((p) => p !== path) } : z
      );
      setInspection({ ...inspection, zones });
    } catch {
      alert("Erreur lors de la suppression");
    }
  };

  return (
    <div data-testid="step-photos">
      <div className="text-sm font-medium text-slate-900 mb-4 flex items-center gap-2">
        <Camera className="h-4 w-4 text-violet-600" /> Photos par zone
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {(inspection.zones || []).map((z) => (
          <button
            key={z.name}
            type="button"
            onClick={() => { if (!disabled) { setActiveZone(z.name); fileRef.current?.click(); } }}
            disabled={disabled}
            className="relative rounded-xl border-2 border-dashed border-slate-200 p-4 text-center hover:border-violet-300 transition-colors group"
            data-testid={`zone-${z.name}`}
          >
            <Camera className="h-5 w-5 text-slate-400 mx-auto group-hover:text-violet-600 transition-colors" />
            <div className="text-xs font-medium text-slate-700 mt-2">{z.name}</div>
            <div className="text-[10px] text-violet-600 font-semibold mt-1">
              {z.photos.length} photo{z.photos.length !== 1 ? "s" : ""}
            </div>
            {z.photos.length > 0 && <div className="absolute top-2 right-2 h-2 w-2 rounded-full bg-emerald-500" />}
          </button>
        ))}
      </div>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleUpload} />
      {uploading && <div className="mt-4 text-sm text-violet-600 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Upload en cours...</div>}

      {/* Photos ajoutées — miniatures réelles, suppression possible uniquement avant toute signature */}
      {(inspection.zones || []).some((z) => z.photos.length > 0) && (
        <div className="mt-5 space-y-3" data-testid="photos-list">
          <div className="text-xs font-semibold text-slate-500">Photos ajoutées</div>
          {(inspection.zones || []).filter((z) => z.photos.length > 0).map((z) => (
            <div key={z.name}>
              <div className="text-[11px] font-medium text-slate-600 mb-1">{z.name}</div>
              <div className="flex flex-wrap gap-2">
                {z.photos.map((p, idx) => (
                  <div key={p} className="relative" data-testid={`photo-chip-${z.name}-${idx}`}>
                    <AuthImg path={p} alt={z.name} className="h-16 w-16 rounded-lg object-cover border border-slate-200" />
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => handleDelete(z.name, p)}
                        className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-slate-900 text-white flex items-center justify-center hover:bg-red-600"
                        data-testid={`photo-delete-${z.name}-${idx}`}
                        title="Supprimer la photo"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Step 3: Damages ───
function StepDamages({ inspection, setInspection, disabled }) {
  const updateZone = (idx, field, value) => {
    const zones = [...inspection.zones];
    zones[idx] = { ...zones[idx], [field]: value };
    setInspection({ ...inspection, zones });
  };

  return (
    <div data-testid="step-damages">
      <div className="text-sm font-medium text-slate-900 mb-4 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-violet-600" /> Dommages et commentaires
      </div>
      <div className="space-y-4">
        {(inspection.zones || []).map((z, i) => (
          <div key={z.name} className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-900">{z.name}</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-xs text-slate-500">Dommage</span>
                <input
                  type="checkbox"
                  checked={z.damages}
                  onChange={(e) => updateZone(i, "damages", e.target.checked)}
                  disabled={disabled}
                  className="rounded border-slate-300 text-violet-600 focus:ring-violet-600"
                />
              </label>
            </div>
            {z.damages && (
              <div className="flex items-center gap-1.5 mb-2" data-testid={`severity-${z.name}`}>
                <span className="text-[10px] uppercase font-bold text-slate-400 mr-1">Gravité</span>
                {[["minor", "Léger", "bg-amber-100 text-amber-700 border-amber-300"], ["medium", "Moyen", "bg-orange-100 text-orange-700 border-orange-300"], ["major", "Important", "bg-red-100 text-red-700 border-red-300"]].map(([k, l, cls]) => (
                  <button
                    key={k}
                    type="button"
                    disabled={disabled}
                    onClick={() => updateZone(i, "severity", z.severity === k ? null : k)}
                    className={`px-2.5 py-1 rounded-lg border text-[11px] font-semibold transition-colors disabled:opacity-60 ${z.severity === k ? cls : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
                    data-testid={`severity-${z.name}-${k}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
            <input
              value={z.comment}
              onChange={(e) => updateZone(i, "comment", e.target.value)}
              disabled={disabled}
              placeholder="Commentaire (optionnel)"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 focus:border-violet-600 outline-none"
            />
          </div>
        ))}
      </div>
      <div className="mt-4">
        <label className="text-sm font-medium text-slate-900 mb-2 block">Commentaire général</label>
        <textarea
          value={inspection.general_comment || ""}
          onChange={(e) => setInspection({ ...inspection, general_comment: e.target.value })}
          disabled={disabled}
          rows={3}
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:ring-2 focus:ring-violet-600 focus:border-violet-600 outline-none resize-none"
          placeholder="Remarques générales..."
          data-testid="general-comment"
        />
      </div>
    </div>
  );
}

// ─── Step 4: Sign ───
function StepSign({ inspection, setInspection, role, alreadySigned }) {
  const [signing, setSigning] = useState(false);
  const [creatingIssues, setCreatingIssues] = useState(false);
  const navigate = useNavigate();

  const handleSign = async (sigData) => {
    setSigning(true);
    try {
      const { data } = await api.put(`/inspections/${inspection.id}`, { step: "sign", signature_data: sigData });
      setInspection(data.inspection);
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur");
    } finally {
      setSigning(false);
    }
  };

  const damagedZones = (inspection.zones || []).filter((z) => z.damages).length;
  const isLocked = !!inspection.client_signature || !!inspection.vendor_signature;
  const canCreateIssues = role === "vendor" && isLocked && damagedZones > 0 && !inspection.issues_created;

  const createIssues = async () => {
    setCreatingIssues(true);
    try {
      const { data } = await api.post(`/inspections/${inspection.id}/create-issues`);
      setInspection({ ...inspection, issues_created: true });
      alert(`${data.issues.length} signalement(s) créé(s). Retrouvez-les dans la fiche du véhicule.`);
      navigate(`/vendor/pilotage?tab=vehicles&view=events`);
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur");
    } finally {
      setCreatingIssues(false);
    }
  };

  return (
    <div data-testid="step-sign">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm font-medium text-slate-900">Récapitulatif</div>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          data-testid="edl-print-btn"
        >
          <Printer className="h-3.5 w-3.5" /> Imprimer
        </button>
      </div>
      <div id="edl-print-area">
        <div className="hidden print:block mb-4">
          <div className="font-heading text-xl font-medium">État des lieux {inspection.type === "departure" ? "de départ" : "de retour"} {inspection.reference ? `— ${inspection.reference}` : ""}</div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm mb-6">
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-slate-500 text-xs">Kilométrage</div>
            <div className="font-medium text-slate-900">{inspection.mileage || "—"} km</div>
          </div>
          {inspection.battery_level != null ? (
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-slate-500 text-xs">Batterie</div>
              <div className="font-medium text-slate-900">{inspection.battery_level} %{inspection.fuel_level ? ` · Carburant ${inspection.fuel_level}` : ""}</div>
            </div>
          ) : (
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-slate-500 text-xs">Carburant</div>
              <div className="font-medium text-slate-900">{inspection.fuel_level || "—"}</div>
            </div>
          )}
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-slate-500 text-xs">Photos</div>
            <div className="font-medium text-slate-900">{inspection.zones?.reduce((s, z) => s + z.photos.length, 0) || 0}</div>
          </div>
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-slate-500 text-xs">Dommages</div>
            <div className="font-medium text-slate-900">{inspection.zones?.filter((z) => z.damages).length || 0} zone(s)</div>
          </div>
        </div>

        {/* Détail des dommages (visible à l'impression) */}
        {damagedZones > 0 && (
          <div className="hidden print:block mb-6 text-sm">
            {(inspection.zones || []).filter((z) => z.damages).map((z) => (
              <div key={z.name}>• {z.name} — {({ minor: "Léger", medium: "Moyen", major: "Important" })[z.severity] || "—"}{z.comment ? ` : ${z.comment}` : ""}</div>
            ))}
          </div>
        )}

        {/* Signatures */}
        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <SigBlock label="Loueur" sig={inspection.vendor_signature} />
          <SigBlock label="Client" sig={inspection.client_signature} />
        </div>
      </div>

      {/* Pont EDL → Signalements */}
      {canCreateIssues && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="edl-create-issues-block">
          <div className="text-sm font-semibold text-slate-900 mb-1">
            {damagedZones} zone{damagedZones > 1 ? "s" : ""} endommagée{damagedZones > 1 ? "s" : ""} constatée{damagedZones > 1 ? "s" : ""}
          </div>
          <p className="text-xs text-slate-600 mb-3">Créez automatiquement un signalement par zone (photos et commentaires repris).</p>
          <button
            onClick={createIssues}
            disabled={creatingIssues}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-50"
            data-testid="edl-create-issues-btn"
          >
            {creatingIssues ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <AlertTriangle className="h-3.5 w-3.5" />} Créer les signalements
          </button>
        </div>
      )}
      {role === "vendor" && inspection.issues_created && (
        <div className="mb-6 text-xs text-emerald-600 flex items-center gap-1.5" data-testid="edl-issues-created-note">
          <CheckCircle2 className="h-4 w-4" /> Signalements créés depuis cet état des lieux.
        </div>
      )}

      {/* Bilan de restitution — EDL retour signé (loueur) */}
      {role === "vendor" && inspection.type === "return" && isLocked && (
        <ReturnSummary bookingId={inspection.booking_id} />
      )}

      {!alreadySigned && (
        <>
          <div className="text-sm text-slate-600 mb-3">Dessinez votre signature :</div>
          <SignaturePad onConfirm={handleSign} disabled={signing} />
        </>
      )}
      {alreadySigned && (
        <div className="text-sm text-emerald-600 flex items-center gap-1.5">
          <CheckCircle2 className="h-4 w-4" /> Vous avez signé cet état des lieux.
        </div>
      )}
    </div>
  );
}

// ─── Bilan de restitution (EDL retour signé) : comparaison départ/retour + actions 1-clic ───
function ReturnSummary({ bookingId }) {
  const [data, setData] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [capturing, setCapturing] = useState(false);
  const [captureMsg, setCaptureMsg] = useState("");

  const load = () =>
    api.get(`/inspections/${bookingId}/compare/summary`).then(({ data }) => setData(data)).catch(() => setData(null));
  useEffect(() => { load(); }, [bookingId]); // eslint-disable-line

  if (!data) return null;
  const problems = data.diffs.filter((d) => d.status === "new" || d.status === "worse");
  const caution = data.caution || {};
  const held = caution.held_amount || 0;
  const franchise = caution.franchise_amount;

  const capture = async (amount) => {
    const val = Math.round(parseFloat(amount) * 100) / 100;
    if (!val || val <= 0) return;
    if (!window.confirm(`Retenir ${val.toFixed(2)} € sur la caution ?`)) return;
    setCapturing(true);
    setCaptureMsg("");
    try {
      await api.post(`/vendor/bookings/${bookingId}/caution/capture`, { amount: val });
      setCaptureMsg(`Retenue de ${val.toFixed(2)} € enregistrée.`);
      load();
    } catch (err) {
      setCaptureMsg(err.response?.data?.detail || "Erreur lors de la retenue");
    } finally {
      setCapturing(false);
    }
  };

  const SEV = { minor: "Léger", medium: "Moyen", major: "Important" };
  return (
    <div className="mb-6 rounded-2xl border border-violet-200 bg-violet-50/40 p-4 sm:p-5" data-testid="edl-return-summary">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className={`h-4 w-4 ${problems.length ? "text-amber-600" : "text-emerald-600"}`} />
        <div className="text-sm font-semibold text-slate-900">Bilan de restitution</div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center mb-4">
        <div className="rounded-xl bg-white border border-slate-200 py-2">
          <div className="text-lg font-semibold text-red-600" data-testid="summary-new-count">{data.new_count}</div>
          <div className="text-[10px] text-slate-500 font-semibold uppercase">Nouveaux dommages</div>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 py-2">
          <div className="text-lg font-semibold text-amber-600" data-testid="summary-worse-count">{data.worse_count}</div>
          <div className="text-[10px] text-slate-500 font-semibold uppercase">Aggravations</div>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 py-2">
          <div className="text-lg font-semibold text-slate-900">{data.mileage?.diff != null ? `+${data.mileage.diff}` : "—"}</div>
          <div className="text-[10px] text-slate-500 font-semibold uppercase">Km parcourus</div>
        </div>
      </div>

      {problems.length > 0 ? (
        <div className="space-y-1.5 mb-4" data-testid="summary-diff-list">
          {problems.map((d) => (
            <div key={d.zone} className="flex items-center justify-between rounded-lg bg-white border border-slate-200 px-3 py-2 text-xs">
              <span className="font-medium text-slate-800">{d.zone}</span>
              <span className={`px-2 py-0.5 rounded-full font-bold text-[10px] ${d.status === "new" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                {d.status === "new" ? `Nouveau · ${SEV[d.to_severity] || "—"}` : `${SEV[d.from_severity] || "—"} → ${SEV[d.to_severity] || "—"}`}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mb-4" data-testid="summary-no-damage">
          Aucun nouveau dommage ni aggravation par rapport au départ.
        </div>
      )}

      {(data.fuel.departure || data.battery.departure != null) && (
        <div className="text-[11px] text-slate-500 mb-4">
          {data.fuel.departure && <>Carburant : {data.fuel.departure} → {data.fuel.return || "—"}. </>}
          {data.battery.departure != null && <>Batterie : {data.battery.departure} % → {data.battery.return ?? "—"} %.</>}
        </div>
      )}

      {/* Retenue de caution */}
      {caution.status === "held" && held > 0 && (
        <div className="rounded-xl bg-white border border-slate-200 p-3 mb-3" data-testid="summary-caution-block">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-800 mb-2">
            <ShieldCheck className="h-3.5 w-3.5 text-amber-600" /> Caution immobilisée : {held.toFixed(2)} €
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {franchise > 0 && franchise <= held && (
              <button
                onClick={() => capture(franchise)}
                disabled={capturing}
                className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-[11px] font-bold disabled:opacity-50"
                data-testid="summary-capture-franchise-btn"
              >
                Retenir la franchise ({franchise.toFixed(0)} €)
              </button>
            )}
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min="1"
                max={held}
                step="0.01"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                placeholder="Montant €"
                className="w-24 h-8 rounded-lg border border-slate-200 px-2 text-xs"
                data-testid="summary-capture-amount-input"
              />
              <button
                onClick={() => capture(customAmount)}
                disabled={capturing || !customAmount}
                className="px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 text-[11px] font-bold disabled:opacity-40"
                data-testid="summary-capture-custom-btn"
              >
                Retenir ce montant
              </button>
            </div>
          </div>
          {captureMsg && <div className="text-[11px] mt-2 font-medium text-slate-600" data-testid="summary-capture-msg">{captureMsg}</div>}
        </div>
      )}
      {caution.status === "captured" && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-3">
          Retenue effectuée : {(caution.captured_amount || 0).toFixed(2)} € sur la caution.
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <a
          href="/vendor/pilotage?create=intervention"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-[11px] font-semibold text-slate-700 hover:border-violet-300"
          data-testid="summary-create-intervention-link"
        >
          <Wrench className="h-3 w-3" /> Créer une intervention
        </a>
        {data.return_issues_created && (
          <a
            href="/vendor/pilotage?tab=vehicles&view=events&events=issues"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-[11px] font-semibold text-slate-700 hover:border-violet-300"
            data-testid="summary-view-issues-link"
          >
            <ArrowUpRight className="h-3 w-3" /> Voir les signalements (→ sinistre)
          </a>
        )}
      </div>
    </div>
  );
}

function SigBlock({ label, sig }) {
  return (
    <div className="rounded-xl border border-slate-200 p-3 bg-slate-50/50">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-1">{label}</div>
      {sig ? (
        <>
          <img src={sig.data_base64} alt="" className="h-12 object-contain" />
          <div className="text-[10px] text-slate-400 mt-1">{sig.signer_name}</div>
        </>
      ) : (
        <div className="h-12 flex items-center text-xs text-slate-400">En attente</div>
      )}
    </div>
  );
}
